// src/components/ScrollContext.tsx

import {
  type FC,
  type ReactNode,
  type RefObject,
  createContext,
  useContext,
  useRef
} from "react"

interface ScrollContextType {
  registerRef: (key: string, ref: React.RefObject<HTMLDivElement | null>) => void
  scrollTo: (key: string) => void
}

const ScrollContext = createContext<ScrollContextType | undefined>(undefined)

interface ScrollProviderProps {
  children: ReactNode
}

export const ScrollProvider: FC<ScrollProviderProps> = ({ children }): JSX.Element => {
  const refs = useRef<Record<string, RefObject<HTMLDivElement | null>>>({})

  const registerRef = (key: string, ref: RefObject<HTMLDivElement | null>): void => {
    refs.current[key] = ref
  }

  const scrollTo = (key: string): void => {
    const ref = refs.current[key]
    if (ref?.current) {
      ref.current.scrollIntoView({ behavior: "smooth" })
    }
  }

  // eslint-disable-next-line react/jsx-no-constructed-context-values
  const contextValue = {
    registerRef,
    scrollTo
  }

  return (
    <ScrollContext.Provider value={contextValue}>
      {children}
    </ScrollContext.Provider>
  )
}

export const useScrollContext = (): ScrollContextType => {
  const context = useContext(ScrollContext)
  if (!context) {
    // Provide fallback behavior
    const fallbackRefs: Record<string, RefObject<HTMLDivElement | null>> = {}

    return {
      registerRef: (key: string, ref: RefObject<HTMLDivElement | null>) => {
        fallbackRefs[key] = ref
      },
      scrollTo: (key: string) => {
        const ref = fallbackRefs[key]
        if (ref?.current) {
          ref.current.scrollIntoView({ behavior: "smooth" })
        }
      }
    }
  }
  return context
}
